import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { TransportMovement } from '@/types';

dayjs.extend(utc);

const parseStoredDateTime = (value?: string | null) => {
  if (!value) return null;
  const date = dayjs.utc(value);
  return date.isValid() ? date.local() : null;
};

export const formatStoredDateTime = (
  value: string | null | undefined,
  format: string,
) => parseStoredDateTime(value)?.format(format) ?? null;

export type LoadSortField =
  | 'loadNumber'
  | 'statusDesc'
  | 'vehicleCount'
  | 'collectionDate'
  | 'fromLocation'
  | 'providerName';

export type LoadSort = {
  field: LoadSortField;
  order: 'ascend' | 'descend';
};

export const DEFAULT_LOAD_SORT: LoadSort = {
  field: 'collectionDate',
  order: 'ascend',
};

export type LoadListFilters = {
  search: string;
  sites: string[];
  transporters: string[];
  status: string | null;
};

export const EMPTY_LOAD_FILTERS: LoadListFilters = {
  search: '',
  sites: [],
  transporters: [],
  status: null,
};

export type StatusTab = {
  status: string;
  count: number;
};

export const MIN_SEARCH_LENGTH = 6;

export type SearchState = {
  term: string;
  isSearching: boolean;
  hint: 'minLength' | 'lettersAndDigits' | null;
};

export const getSearchState = (text: string): SearchState => {
  const term = text.trim().toUpperCase();
  if (!term) return { term, isSearching: false, hint: null };
  if (!/^[A-Z0-9]+$/.test(term)) {
    return { term, isSearching: false, hint: 'lettersAndDigits' };
  }
  if (term.length < MIN_SEARCH_LENGTH) {
    return { term, isSearching: false, hint: 'minLength' };
  }
  return { term, isSearching: true, hint: null };
};

export const filterLoads = (
  loads: TransportMovement[],
  { sites, transporters, status }: LoadListFilters,
) => {
  const siteSet = new Set(sites);
  const transporterSet = new Set(transporters);
  const withoutStatus = loads.filter(
    (load) =>
      (!siteSet.size || siteSet.has(load.fromLocation ?? '')) &&
      (!transporterSet.size || transporterSet.has(load.providerName ?? '')),
  );
  const visible = status
    ? withoutStatus.filter((load) => load.statusDesc === status)
    : withoutStatus;
  return { withoutStatus, visible };
};

const FIXED_STATUS_TABS = ['Planned', 'Await Despatch', 'Despatched'];

export const getStatusTabs = (
  allLoads: TransportMovement[],
  countedLoads: TransportMovement[],
  selectedStatus: string | null,
): StatusTab[] => {
  const otherCodes = new Map<string, string>();
  allLoads.forEach((load) => {
    const status = load.statusDesc;
    if (
      status &&
      !FIXED_STATUS_TABS.includes(status) &&
      !otherCodes.has(status)
    ) {
      otherCodes.set(status, load.loadStatus ?? '');
    }
  });
  if (
    selectedStatus &&
    !FIXED_STATUS_TABS.includes(selectedStatus) &&
    !otherCodes.has(selectedStatus)
  ) {
    otherCodes.set(selectedStatus, '');
  }

  const counts = new Map<string, number>();
  countedLoads.forEach((load) => {
    if (load.statusDesc) {
      counts.set(load.statusDesc, (counts.get(load.statusDesc) ?? 0) + 1);
    }
  });

  const otherStatuses = [...otherCodes.entries()]
    .sort(([descA, codeA], [descB, codeB]) =>
      codeA === codeB ? descA.localeCompare(descB) : codeA.localeCompare(codeB),
    )
    .map(([status]) => status);

  return [...FIXED_STATUS_TABS, ...otherStatuses].map((status) => ({
    status,
    count: counts.get(status) ?? 0,
  }));
};

const sortValue = (
  load: TransportMovement,
  field: LoadSortField,
): string | number | null => {
  const value = load[field];
  if (value === null || value === undefined || value === '') return null;
  if (field === 'collectionDate') {
    return parseStoredDateTime(value as string)?.valueOf() ?? null;
  }
  return value;
};

export const sortLoads = (
  loads: TransportMovement[],
  { field, order }: LoadSort,
) => {
  const direction = order === 'ascend' ? 1 : -1;
  return loads
    .map((load) => ({ load, key: sortValue(load, field) }))
    .sort(({ load: a, key: keyA }, { load: b, key: keyB }) => {
      if (keyA === null && keyB === null) {
        return a.loadNumber.localeCompare(b.loadNumber);
      }
      if (keyA === null) return 1;
      if (keyB === null) return -1;
      const result =
        typeof keyA === 'number' && typeof keyB === 'number'
          ? keyA - keyB
          : String(keyA).localeCompare(String(keyB), undefined, {
              numeric: true,
            });
      return result === 0
        ? a.loadNumber.localeCompare(b.loadNumber)
        : result * direction;
    })
    .map(({ load }) => load);
};

export const getDistinctOptions = (
  loads: TransportMovement[],
  key: 'fromLocation' | 'providerName',
) =>
  [
    ...new Set(
      loads
        .map((load) => load[key])
        .filter((value): value is string => !!value),
    ),
  ]
    .sort((a, b) => a.localeCompare(b))
    .map((value) => ({ label: value, value }));

export type LoadStatusTone =
  | 'planned'
  | 'awaitingDispatch'
  | 'dispatched'
  | 'other';

export const getLoadStatusTone = (
  statusDesc?: string | null,
): LoadStatusTone => {
  const status =
    statusDesc?.toLowerCase().replace('despatch', 'dispatch') ?? '';
  if (status.includes('await') && status.includes('dispatch')) {
    return 'awaitingDispatch';
  }
  if (status.includes('dispatched')) return 'dispatched';
  if (status.includes('planned')) return 'planned';
  return 'other';
};

export const formatCollection = (
  value: string | null | undefined,
  labels: {
    today: string;
    tomorrow: string;
    yesterday: string;
    separator?: string;
  },
  now = dayjs(),
) => {
  const date = parseStoredDateTime(value);
  if (!date) return null;
  const time = date.format('HH:mm');
  const separator = labels.separator ?? ' ';
  if (date.isSame(now, 'day')) return `${labels.today}${separator}${time}`;
  if (date.isSame(now.add(1, 'day'), 'day')) {
    return `${labels.tomorrow}${separator}${time}`;
  }
  if (date.isSame(now.subtract(1, 'day'), 'day')) {
    return `${labels.yesterday}${separator}${time}`;
  }
  return date.format('DD/MM/YYYY HH:mm');
};
